import React, { useContext, useEffect, useState } from 'react';
import { Building, Check2Circle, Check2Square, Clipboard2, Pencil, Person, PersonFill, PersonPlus, Square, XCircle } from 'react-bootstrap-icons';
import Authentication from '../../../../libs/api/services/Authentication';
import Facility from '../../../../libs/api/services/Facility';
import User from '../../../../libs/api/services/User';
import WorkFlow from '../../../../libs/api/services/WorkFlow';
import WorkFlowService from '../../../../libs/api/services/WorkFlowService';
import CommonFunctionsContext from '../../../common/CommonFunctionsProvider';
import { FacilitiesSection } from './facility_section/FacilitiesSection';
import TasksSection from './tasks_section/TasksSection';
import { UserDetailsSection } from './user_profile/UserDetailsSection';

export const UserManagementContext = React.createContext();

export const UserManagementProvider = ({ children }) => {

    const { setSectionInfo, isNullOrEmpty, showError, setLoading, asyncLoader } = useContext(CommonFunctionsContext)
    const [state, setState] = useState({
        screens: [
            {
                name: 'Details',
                icon: <Person fontSize={35} />,
                complete: false,
                component: <UserDetailsSection />,
                onChanged: (s) => saveUser(s)
            },
            {
                name: 'Facility / Roles',
                icon: <Building fontSize={35} />,
                complete: true,
                component: <FacilitiesSection />
            },
            {
                name: 'Tasks',
                icon: <Clipboard2 fontSize={35} />,
                complete: true,
                component: <TasksSection />
            },
            {
                name: 'Status',
                icon: <PersonFill fontSize={35} />,
                complete: true,
                component: <StatusSection />
            }
        ],
        currentScreen: -1,
        user: undefined
    })
    const setSection = async (s) => {
        setLoading(true)
        try {
            if (state.screens[state.currentScreen].onChanged)
                setState({ ...state, ...await state.screens[state.currentScreen].onChanged(state), currentScreen: s })
            else
                setState({ ...state, currentScreen: s })
        } catch (e) { console.error(e) }
        setLoading(false)
    }

    const _validate = (user, roles, tasks, status) => {
        var _screens = state.screens
        // Reset if no user selected
        // if (!state.user)
        for (var i = 0; i < _screens.length; i++)_screens[i].complete = false
        if (
            !isNullOrEmpty(user.firstName) &&
            !isNullOrEmpty(user.lastName) &&
            !isNullOrEmpty(user.idNumber) &&
            !isNullOrEmpty(user.password) &&
            !isNullOrEmpty(user.emailAddress) &&
            !isNullOrEmpty(user.mobileNumber)
        ) _screens[0].complete = true

        if (
            roles && roles.activeRoles.length > 0
        ) _screens[1].complete = true

        // Tasks
        _screens[2].complete = true
        if (tasks && tasks.tasks)
            for (const t of tasks.tasks)
                if (t.triggerHistory && t.triggerHistory.length === 0) {
                    _screens[2].complete = false
                    break;
                }

        // Status
        _screens[3].complete = true
        if (status && status.items)
            for (const i of status.items)
                if (!i.isComplete) {
                    _screens[3].complete = false
                    break;
                }
        setState({ ...state, screens: _screens })
    }

    useEffect(() => {
        const load = async () => {
            // await loadFacilityRoles()
            // loadUserProfile(state.user)
            // is new user
            // Pre-validate fields
            _validate(state.user, state.roles, state.tasks, state.status)
        }
        if (state.user) {
            load()
            // Set top text
            setSectionInfo(`Managing ${!state.user.firstName && !state.user.lastName ? 'a new user' : ''} ${state.user.firstName ? state.user.firstName : ''} ${state.user.lastName ? state.user.lastName : ''} ${Authentication.getFacilityName() ? `at ${Authentication.getFacilityName()}` : ''}`)
        }
        else
            setSectionInfo()
    }, [state.user, state.roles])

    const loadUserProfile = async (user) => {
        if (!user) return
        setLoading(true)
        // Load User

        // Load Roles
        // console.log('loadUserProfile',)
        setState({
            ...state,
            ...await loadFacilityRoles(user),
            ...await loadUserTasks(user),
            ...await loadUserStatus(user),
            currentScreen: state.currentScreen < 0 ? 0 : state.currentScreen,
            user: user
        })
        setLoading(false)
    }

    // const setUser = (u) => console.log('setUser', u)
    const setUser = (u) =>
        u.id === -1 ? setState({
            ...state, currentScreen: state.currentScreen < 0 ? 0 :
                state.currentScreen, user: u, roles: { ...state.roles, activeRoles: [] }
        }) : loadUserProfile(u)
    const reset = (u) => { setState({ ...state, currentScreen: -1, user: undefined }) }

    const saveUser = async (state) => {
        var res = undefined
        if (state.user.id === -1)
            res = await User.add({ ...state.user, id: undefined }).catch(e => showError(e))
        else
            res = await User.edit(state.user.id, state.user).catch(e => showError(e))
        if (!res) throw Error('Failed to update User')
        // console.log('Save', { ...state, user: res, ...await loadFacilityRoles(res), })
        return { user: res, ...await loadFacilityRoles(res), }
    }

    const loadFacilityRoles = async (user) => {
        // Load all facilities
        const fcs = Authentication.isClaimValid("System Admin") !== undefined ? await Facility.list()
            : (await Facility.list()).filter(x => x.id === parseInt(Authentication.getFacility()))
        // Load all roles types
        var fac_roles = []
        const UserDetails = await User.get(user.id);
        if (UserDetails && UserDetails.userFacilitieRoles)
            for (const facility of fcs)
                fac_roles = [...fac_roles, ...UserDetails.userFacilitieRoles.filter(x => x.facility.id === parseInt(facility.id))]
        // Filter current users roles
        return ({
            roles: {
                facilities: fcs,
                activeRoles: fac_roles
            }
        })
    }
    const loadUserTasks = async (user) => {
        // Load all facilities
        return ({
            tasks: await WorkFlow
                .GetSystemTaskDetails(user.id, Authentication.getFacility(), "System - User Management Tasks",)
                .catch(e => showError(e))
        })
    }
    const loadUserStatus = async (user) => {
        // Load all facilities
        return ({
            status: await User.GetUserStatus(user.id)
        })
    }

    const allocRevokeRole = (role) => {
        const _roles = state.roles.activeRoles.filter(x => x.id !== role.id)
        User.addFacility(state.user.id, role.facility.id, role.roles.id)
            .then((r) => setState({ ...state, roles: { ...state.roles, activeRoles: _roles } }))
    }
    const allocRole = (facility, role) => {
        // console.log(state.roles.activeRoles)
        const _roles = [...state.roles.activeRoles, { facility: facility, roles: role }]
        User.addFacility(state.user.id, facility.id, role.id)
            .then((r) => setState({ ...state, roles: { ...state.roles, activeRoles: _roles } }))
    }
    const RunTask = (TaskID) => {
        asyncLoader(async () => await WorkFlowService.RunWorkFlowTask(TaskID, {
            // "WorkFlowEventID": r.workflowID,
            "InitiatedByUser": String(Authentication.UserId()),
            "userId": String(state.user.id),
            "facility": String(Authentication.getFacility()),
            // "clinician": options.clinician ? options.clinician : _loggedInClinician,
            // "admin": options.admin ? options.admin : _loggedInAdmin,
            // "questionnaire": options.questionnaire,
            // "assignment": options.assignment,
            // "patient": options.patient,
            // "calendar": options.calendar,
            // "calendarEvent": options.calendarEvent,
        }).then(() => loadUserProfile(state.user)))

    }

    return (<UserManagementContext.Provider value={{
        MANAGEMENT_SCREENS: state.screens, state, setState, setSection, setUser, reset, saveUser, allocRevokeRole, allocRole, asyncLoader, RunTask
    }}>{children}</UserManagementContext.Provider>)
}

export default function UserManagementScreen() {
    return (
        <UserManagementProvider>
            <UserWizardLayout>
                <UserManagmentSelection />
            </UserWizardLayout>
        </UserManagementProvider>
    )
}

const UserList = () => {
    const [users, setUsers] = useState([])
    const [searched, setSearched] = useState([])
    const { setUser, asyncLoader } = useContext(UserManagementContext)
    useEffect(() => {
        const load = async () =>
            asyncLoader(async () => setUsers(await User.GetUsersByFacility(Authentication.getFacility())))

        load()
    }, [])

    useEffect(() => { if (users) setSearched(users) }, [users])

    const _handleSearch = (s) => {
        var res = []
        items: for (const e of users)
            for (const x of Object.keys(e))
                if ((typeof e[x] === 'string') && e[x].toLowerCase().includes(s.toLowerCase())) {
                    // || typeof e[x] === 'number'
                    res.push(e)
                    continue items
                }
        setSearched(res)
    }
    const _handleCreateNewUser = (e) => {
        setUser({ id: -1 })
    }

    return (<div className='m-1 p-2 rounded d-flex flex-column h-100 pb-5'>
        <div className='flex-shrink-1 d-flex bg-secondary-container p-2 rounded shadow'>
            <input onChange={(e) => _handleSearch(e.target.value)} className='form-control' placeholder='Search' />
            <button onClick={_handleCreateNewUser} className='btn w-25'><PersonPlus className='me-2' />Create New User</button>
        </div>
        <div className='flex-grow-1 mt-2' style={{ overflow: 'auto' }}>
            <table className=' w-100 rounded'>
                <thead>
                    <tr>
                        <th></th>
                        <th>ID Number</th>
                        <th>First Name</th>
                        <th>Last Names</th>
                        <th>Roles</th>
                        <th>Accepted T&C's</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        searched && searched.map((u) =>
                            <tr onClick={() => setUser(u)} style={{ cursor: 'pointer' }}>
                                <td><Person /></td>
                                <td>{u.idNumber}</td>
                                <td>{u.firstName}</td>
                                <td>{u.lastName}</td>
                                <td>{u.userFacilitieRoles ? u.userFacilitieRoles.length : 0}</td>
                                <td>{u.acceptedTnC ? <Check2Circle /> : <XCircle />}</td>
                                <td><Pencil /></td>
                            </tr>)
                    }
                </tbody>
            </table>
        </div>
    </div>
    )
    //return (JSON.stringify(users))
    // GetUsersByFacility
}

const UserWizardLayout = ({ children }) => {
    const { MANAGEMENT_SCREENS, state, setSection, reset } = useContext(UserManagementContext)
    return (
        state.currentScreen < 0 ?
            children
            :
            <div className='d-flex flex-column h-100'>
                {/* <h3 className='text-center'>User Management</h3> */}
                <div style={{ marginTop: '-20px', background: '#3572A1' }} className='shadow p-3 '>
                    <div className='text-center text-light'>
                        <h3><PersonFill /> {state.user.firstName} {state.user.lastName}</h3>
                    </div>
                    <div className='d-flex align-items-start justify-content-start '>
                        {MANAGEMENT_SCREENS.map((t, i) => <WizardSectionTab key={`tabl-${i}`} screen={t} idx={i} />)}
                    </div>
                </div>
                <div className='flex-grow-1 mt-3' style={{ overflow: 'auto' }}>{
                    state.currentScreen > -1 ?
                        MANAGEMENT_SCREENS[state.currentScreen].component
                        :
                        children
                }</div>
                <div className='d-flex justify-content-center'>
                    {state.currentScreen > 0 && <button onClick={() => setSection(state.currentScreen - 1)} className='btn bg-primary-container'>Back</button>}
                    <button onClick={() => reset()} className='btn bg-primary-container'>Cancel</button>
                    {state.currentScreen < state.screens.length - 1 ? <button onClick={() => setSection(state.currentScreen + 1)} className='btn bg-primary-container'>Next</button> : <button onClick={() => reset()} className='btn bg-primary-container'>Done</button>}
                </div>
            </div>)
}

const WizardSectionTab = ({ screen, idx }) => {
    const { setSection } = useContext(UserManagementContext)
    return (<button style={{ width: '150px' }} onClick={() => setSection(idx)} className={`d-flex me-5 flex-column align-items-center px-2 btn position-relative text-shadow  ${screen.complete ? 'text-success-container' : 'text-light'}`} >
        <div className={`position-relative`}>
            <div style={{ position: 'absolute', right: '-15px', top: '-15px' }}>
                {screen.complete ? <Check2Circle /> : <XCircle />}
            </div>
            {screen.icon}
        </div>
        {screen.name}
    </button>)
}

const UserManagmentSelection = () => {
    const { setUser } = useContext(UserManagementContext)
    const [state, setSate] = useState({
        users: []
    })
    const _handleSearch = (s) => {
        User.search({
            search: s,
            facility: Authentication.getFacility()
        }).then((r) => setSate({ ...state, users: r }))
    }
    return (
        <>
            <h3 className='text-center'>User Management</h3>
            <UserList />
            {/* <div className="row">
                <div className="col-lg-10">
                    <input
                        onChange={(e) => _handleSearch(e.target.value)}
                        // ref={refSearch}
                        // onFocus={(e) => refSearch.current.placeholder = 'Start typing users name to start searching'}
                        // onBlur={(e) => _handleNoResult()}
                        // onKeyUp={(e) => _handleKeyUp(e)}
                        // onChange={(e) => search(e.target.value)} defaultValue={details} 
                        className='form-control bg-search-bar rounded-3 text-primary'
                        placeholder='User Search' />
                </div>
                <div className="col-lg-2">
                    <button onClick={(e) => { }} className="btn bg-buttons">Add User</button>
                </div>
            </div> */}
            {state.users.map((u) =>
                <button key={u.id} className="d-flex mx-2 btn  btn-xs" onClick={() => setUser(u)}>
                    <div className='flex-shrink-1'><Person /></div>
                    <div className='flex-grow-1 d-flex'>
                        <div className='mx-2'>{u.firstName} {u.lastName}</div>
                        <div className='mx-2'></div>
                    </div>
                    {/* <div className=''>Options</div> */}
                </button>
            )}
        </>
    )
}

const StatusSection = () => {
    const { state } = useContext(UserManagementContext)

    return <div className='text-center'>
        <h3>User Status</h3>
        <div className='w-100 px-3'>
            {state.status &&
                state.status.items.map((i) =>
                    <div>
                        <div>
                            <div className='d-flex justify-content-between'><div><Clipboard2 size={20} />{i.status}</div> <div>{i.isComplete ? <Check2Square size={20} /> : <Square size={20} />}</div></div>
                        </div>
                        <div>
                            {i.options && i.options.map((o) => <button className='btn'>{o.description}</button>)}
                        </div>
                    </div>
                )
            }
        </div>
    </div>
}